import {
  Activity,
  AlertTriangle,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Clock3,
  Database,
  History,
  Loader2,
  RefreshCw,
  Sparkles,
  Workflow,
  XCircle,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, type BadgeColor } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import type {
  CreatePanopticonBackfillRequest,
  PanopticonBackfillRun,
  PanopticonPattern,
  PanopticonRunOutcome,
  PanopticonTaskOutcome,
} from '@/services/api/panopticonService';
import {
  useCancelPanopticonBackfillMutation,
  useCreatePanopticonBackfillMutation,
  useGetPanopticonBackfillsQuery,
  useGetPanopticonCoverageDetailQuery,
  useGetPanopticonCoveragesQuery,
  useGetPanopticonPatternsQuery,
  useGetPanopticonRunDetailQuery,
  useGetPanopticonTaskDetailQuery,
} from '@/services/api/panopticonService';

const BACKFILL_MAX_CONCURRENT_COVERAGES = 50;

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function truncate(value: string | null | undefined, max = 160): string {
  if (!value) return '—';
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

function shortId(value: string | null | undefined): string {
  if (!value) return '—';
  return value.slice(0, 8);
}

function matterLabel(
  matterName: string | null | undefined,
  matterId: string | null | undefined,
): string {
  if (matterName && matterName.trim()) return matterName;
  if (matterId) return `Matter ${shortId(matterId)}`;
  return 'Matter —';
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

function asArray<T = Record<string, unknown>>(value: unknown): T[] {
  if (!Array.isArray(value)) return [];
  return value as T[];
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function toDatetimeLocalValue(date: Date): string {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function toIsoString(value: string): string {
  return new Date(value).toISOString();
}

const PATTERN_SPEC_LABELS: Record<string, string> = {
  'task.open_claim.v1': 'Open Claim',
  'task.send_lor.v1': 'Send LOR',
  'task.lor_acknowledgement.v1': 'LOR Acknowledgement',
  'agentflow.crashing_out.run.v1': 'Crashing Out Run',
  'coverage.crashing_out_rollup.v1': 'Coverage Rollup',
};

const PATTERN_WINDOW_ORDER: Record<string, number> = {
  '24h': 0,
  '7d': 1,
  '30d': 2,
};

const HEALTH_STATUS_ORDER: Record<string, number> = {
  blocked: 0,
  at_risk: 1,
  healthy: 2,
};

const DEFAULT_SORT_DIRECTION: Record<CoverageSortKey, SortDirection> = {
  coverage: 'asc',
  matter: 'asc',
  health: 'asc',
  attention: 'desc',
  coverage_status: 'asc',
  latest_run: 'asc',
  evaluated_at: 'desc',
};

type CoverageSortKey =
  | 'coverage'
  | 'matter'
  | 'health'
  | 'attention'
  | 'coverage_status'
  | 'latest_run'
  | 'evaluated_at';

type SortDirection = 'asc' | 'desc';

function humanizePatternValue(value: string | null | undefined): string {
  if (!value) return 'Unknown';

  return value
    .replace(/[._]+/g, ' ')
    .replace(/\b3p\b/gi, '3P')
    .replace(/\blor\b/gi, 'LOR')
    .replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

function formatPatternSpec(value: string | null | undefined): string {
  if (!value) return 'Unknown';
  return PATTERN_SPEC_LABELS[value] ?? humanizePatternValue(value);
}

function resolvePatternLeadText(pattern: PanopticonPattern): string | null {
  const taggedLeadText = (
    asArray<{
      reasoning?: string | null;
      summary?: string | null;
    }>(pattern.sample_findings)
      .map((finding) => finding.reasoning?.trim() || finding.summary?.trim() || null)
      .find((value): value is string => Boolean(value))
  );

  if (taggedLeadText) {
    return taggedLeadText;
  }

  return (
    asArray<Record<string, unknown>>(pattern.sample_outcomes)
      .map((outcome) => {
        const summary = asString(asRecord(outcome).summary)?.trim();
        return summary || null;
      })
      .find((value): value is string => Boolean(value))
    ?? null
  );
}

function resolvePatternTitle(pattern: PanopticonPattern): string {
  if (pattern.tag_display_name) return pattern.tag_display_name;
  if (pattern.tag_slug) return humanizePatternValue(pattern.tag_slug);
  if (pattern.reason_code) return humanizePatternValue(pattern.reason_code);
  return 'Pattern';
}

function resolvePatternTagKind(pattern: PanopticonPattern): 'defect' | 'outcome' {
  return pattern.tag_kind === 'outcome' ? 'outcome' : 'defect';
}

function verdictBadgeColor(verdict: string): BadgeColor {
  switch (verdict) {
    case 'success':
      return 'green';
    case 'issues':
      return 'amber';
    case 'opened_in_sla':
    case 'sent_in_sla':
    case 'acknowledged_in_sla':
    case 'completed_expected_work':
    case 'coverage_stable':
      return 'green';
    case 'failed_execution':
    case 'timed_out':
    case 'coverage_blocked':
      return 'red';
    case 'opened_out_of_sla':
    case 'opened_with_other_issues':
    case 'sent_out_of_sla':
    case 'send_lor_with_other_issues':
    case 'acknowledged_out_of_sla':
    case 'acknowledgement_with_other_issues':
    case 'terminated_by_manual_takeover':
    case 'completed_with_concerns':
    case 'coverage_needs_attention':
      return 'amber';
    case 'send_lor_not_applicable':
    case 'send_lor_closed':
      return 'zinc';
    default:
      return 'zinc';
  }
}

function healthBadgeColor(status: string): BadgeColor {
  switch (status) {
    case 'blocked':
      return 'red';
    case 'at_risk':
      return 'amber';
    case 'healthy':
      return 'green';
    default:
      return 'zinc';
  }
}

function backfillBadgeColor(status: string): BadgeColor {
  switch (status) {
    case 'running':
      return 'blue';
    case 'completed':
      return 'green';
    case 'failed':
    case 'cancelled':
      return 'red';
    default:
      return 'zinc';
  }
}

const JsonPanel: React.FC<{
  label: string;
  value: unknown;
  defaultOpen?: boolean;
}> = ({ label, value, defaultOpen = false }) => (
  <details
    className="rounded-2xl border border-zinc-200 bg-zinc-50/80 p-3 dark:border-zinc-800 dark:bg-zinc-950"
    open={defaultOpen}
  >
    <summary className="cursor-pointer text-sm font-medium text-zinc-700 dark:text-zinc-300">
      {label}
    </summary>
    <pre className="mt-3 overflow-x-auto rounded-xl bg-zinc-950 px-3 py-3 text-xs leading-5 text-zinc-100">
      {JSON.stringify(value ?? null, null, 2)}
    </pre>
  </details>
);

const StatCard: React.FC<{
  label: string;
  value: string | number;
  tone?: 'default' | 'attention';
}> = ({ label, value, tone = 'default' }) => (
  <div
    className={[
      'rounded-2xl border p-4',
      tone === 'attention'
        ? 'border-orange-200 bg-orange-50 dark:border-orange-900/50 dark:bg-orange-950/30'
        : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900',
    ].join(' ')}
  >
    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
      {label}
    </div>
    <div className="mt-2 text-2xl font-semibold text-zinc-950 dark:text-zinc-50">
      {value}
    </div>
  </div>
);

const OutcomeRow: React.FC<{
  label: string;
  value: string | number | null | undefined;
}> = ({ label, value }) => (
  <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-950">
    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
      {label}
    </div>
    <div className="mt-1 text-sm text-zinc-800 dark:text-zinc-200">
      {value ?? '—'}
    </div>
  </div>
);

const CoverageSortHeader: React.FC<{
  active: boolean;
  direction: SortDirection;
  label: string;
  onClick: () => void;
}> = ({ active, direction, label, onClick }) => {
  let icon = <ArrowUpDown className="h-3.5 w-3.5 opacity-60" />;
  if (active) {
    icon = direction === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5" />
      : <ChevronDown className="h-3.5 w-3.5" />;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
    >
      {label}
      {icon}
    </button>
  );
};

const TaskListItem: React.FC<{
  task: PanopticonTaskOutcome;
  selected: boolean;
  onSelect: () => void;
}> = ({ task, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={[
      'w-full rounded-2xl border p-3 text-left transition',
      selected
        ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20'
        : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900',
    ].join(' ')}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-zinc-950 dark:text-zinc-50">
          {task.task_type}
        </div>
        <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          Revision {task.terminal_revision} • {shortId(task.task_id)}
        </div>
      </div>
      <Badge color={verdictBadgeColor(task.verdict)}>{task.verdict}</Badge>
    </div>
  </button>
);

const RunListItem: React.FC<{
  run: PanopticonRunOutcome;
  selected: boolean;
  onSelect: () => void;
}> = ({ run, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={[
      'w-full rounded-2xl border p-3 text-left transition',
      selected
        ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/20'
        : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900',
    ].join(' ')}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-zinc-950 dark:text-zinc-50">
          {run.agent_type}
        </div>
        <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          {run.status} • {shortId(run.run_id)}
        </div>
      </div>
      <Badge color={verdictBadgeColor(run.verdict)}>{run.verdict}</Badge>
    </div>
  </button>
);

const BackfillRow: React.FC<{
  backfill: PanopticonBackfillRun;
  onCancel: (runId: string) => void;
  cancelling: boolean;
}> = ({ backfill, onCancel, cancelling }) => {
  let statusNote: React.ReactNode = null;
  if (backfill.error) {
    statusNote = (
      <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
        {backfill.error}
      </div>
    );
  } else if (backfill.status === 'completed' && backfill.total_coverages === 0) {
    statusNote = (
      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
        No matching Panopticon trigger events were found in the selected time window and scope, so this backfill had nothing to start.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge color={backfillBadgeColor(backfill.status)}>{backfill.status}</Badge>
            <Badge color="zinc">{backfill.scope}</Badge>
            <Badge color="zinc">
              {backfill.processed_coverages}/{backfill.total_coverages} coverages
            </Badge>
            <Badge color="blue">{backfill.processed_snapshots}/{backfill.total_snapshots} evals</Badge>
            <Badge color={backfill.workflow_id ? 'teal' : 'zinc'}>
              {backfill.workflow_id ? 'workflow' : 'inline'}
            </Badge>
          </div>
          <div className="mt-3 font-mono text-xs text-zinc-500 dark:text-zinc-400">
            {backfill.id}
          </div>
          {backfill.workflow_id ? (
            <div className="mt-2 font-mono text-xs text-zinc-500 dark:text-zinc-400">
              {backfill.workflow_id}
            </div>
          ) : null}
          <div className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
            {formatDateTime(backfill.window_start_at)} to {formatDateTime(backfill.window_end_at)}
          </div>
        </div>

        {backfill.status === 'running' && backfill.workflow_id ? (
          <Button
            outline
            onClick={() => {
              onCancel(backfill.id);
            }}
            disabled={cancelling}
          >
            <XCircle data-slot="icon" />
            Cancel
          </Button>
        ) : null}
      </div>

      {statusNote}
    </div>
  );
};

const PatternRow: React.FC<{ pattern: PanopticonPattern }> = ({ pattern }) => {
  const leadText = resolvePatternLeadText(pattern);
  const tagKind = resolvePatternTagKind(pattern);
  const specLabel = formatPatternSpec(pattern.spec_id ?? pattern.reason_namespace);
  const tagKindLabel = tagKind === 'defect' ? 'Defect' : 'Outcome';
  const title = resolvePatternTitle(pattern);
  const secondaryLabel = humanizePatternValue(pattern.tag_slug ?? pattern.reason_code);

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex flex-wrap items-center gap-2">
        <Badge color="zinc">{pattern.window_name}</Badge>
        <Badge color="blue">{pattern.count} hits</Badge>
        <Badge color={tagKind === 'defect' ? 'amber' : 'green'}>{tagKindLabel}</Badge>
      </div>
      <div className="mt-3 text-sm font-semibold text-zinc-950 dark:text-zinc-50">
        {title}
      </div>
      <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
        {specLabel} • {secondaryLabel}
      </div>
      {pattern.tag_description ? (
        <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
          {pattern.tag_description}
        </div>
      ) : null}
      {leadText ? (
        <div className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
          {truncate(leadText, 180)}
        </div>
      ) : null}
    </div>
  );
};

export const ClaimsPanopticonPage: React.FC = () => {
  const [selectedCoverageId, setSelectedCoverageId] = React.useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = React.useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);
  const [coverageSortKey, setCoverageSortKey] = React.useState<CoverageSortKey>('evaluated_at');
  const [coverageSortDirection, setCoverageSortDirection] = React.useState<SortDirection>('desc');
  const [windowStart, setWindowStart] = React.useState(() =>
    toDatetimeLocalValue(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
  );
  const [windowEnd, setWindowEnd] = React.useState(() =>
    toDatetimeLocalValue(new Date()),
  );
  const [dryRun, setDryRun] = React.useState(false);
  const [inline, setInline] = React.useState(false);
  const [useReadReplica, setUseReadReplica] = React.useState(true);
  const [maxConcurrentCoverages, setMaxConcurrentCoverages] = React.useState(1);
  const [scopeToSelectedCoverage, setScopeToSelectedCoverage] = React.useState(false);

  const {
    data: coverageList,
    isLoading: isLoadingCoverages,
    isFetching: isFetchingCoverages,
    refetch: refetchCoverages,
  } = useGetPanopticonCoveragesQuery(undefined, {
    pollingInterval: 15000,
  });
  const coverages = React.useMemo(
    () => coverageList?.coverages ?? [],
    [coverageList],
  );
  const sortedCoverages = React.useMemo(() => {
    const directionMultiplier = coverageSortDirection === 'asc' ? 1 : -1;

    const compareStrings = (left: string | null | undefined, right: string | null | undefined) =>
      (left ?? '').localeCompare(right ?? '', undefined, { sensitivity: 'base' });

    const compareDates = (left: string | null | undefined, right: string | null | undefined) =>
      (new Date(left ?? 0).getTime() - new Date(right ?? 0).getTime());

    return [...coverages].sort((left, right) => {
      let comparison = 0;

      switch (coverageSortKey) {
        case 'coverage':
          comparison = compareStrings(left.coverage_id, right.coverage_id);
          break;
        case 'matter':
          comparison = compareStrings(
            matterLabel(left.matter_name, left.matter_id),
            matterLabel(right.matter_name, right.matter_id),
          );
          break;
        case 'health':
          comparison =
            (HEALTH_STATUS_ORDER[left.health_status] ?? Number.MAX_SAFE_INTEGER)
            - (HEALTH_STATUS_ORDER[right.health_status] ?? Number.MAX_SAFE_INTEGER);
          break;
        case 'attention':
          comparison = Number(left.needs_attention) - Number(right.needs_attention);
          break;
        case 'coverage_status':
          comparison = compareStrings(left.coverage_status, right.coverage_status);
          break;
        case 'latest_run':
          comparison = compareStrings(left.latest_run_id, right.latest_run_id);
          break;
        case 'evaluated_at':
          comparison = compareDates(left.evaluated_at, right.evaluated_at);
          break;
        default:
          comparison = 0;
      }

      if (comparison !== 0) return comparison * directionMultiplier;

      return compareDates(right.evaluated_at, left.evaluated_at);
    });
  }, [coverageSortDirection, coverageSortKey, coverages]);

  const handleCoverageSort = React.useCallback((nextKey: CoverageSortKey) => {
    if (coverageSortKey === nextKey) {
      setCoverageSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setCoverageSortKey(nextKey);
    setCoverageSortDirection(DEFAULT_SORT_DIRECTION[nextKey]);
  }, [coverageSortKey]);

  React.useEffect(() => {
    if (sortedCoverages.length === 0) {
      setSelectedCoverageId(null);
      return;
    }
    if (!selectedCoverageId || !sortedCoverages.some((coverage) => coverage.coverage_id === selectedCoverageId)) {
      setSelectedCoverageId(sortedCoverages[0].coverage_id);
    }
  }, [selectedCoverageId, sortedCoverages]);

  const {
    data: coverageDetail,
    isLoading: isLoadingCoverageDetail,
    isFetching: isFetchingCoverageDetail,
    refetch: refetchCoverageDetail,
  } = useGetPanopticonCoverageDetailQuery(selectedCoverageId ?? '', {
    skip: !selectedCoverageId,
  });

  const selectedCoverage = coverageDetail?.coverage ?? null;
  const taskOutcomes = React.useMemo(
    () => selectedCoverage?.task_outcomes ?? [],
    [selectedCoverage],
  );
  const runOutcomes = React.useMemo(
    () => selectedCoverage?.run_outcomes ?? [],
    [selectedCoverage],
  );

  React.useEffect(() => {
    if (taskOutcomes.length === 0) {
      setSelectedTaskId(null);
      return;
    }
    if (!selectedTaskId || !taskOutcomes.some((task) => task.task_id === selectedTaskId)) {
      setSelectedTaskId(taskOutcomes[0].task_id);
    }
  }, [selectedTaskId, taskOutcomes]);

  React.useEffect(() => {
    if (runOutcomes.length === 0) {
      setSelectedRunId(null);
      return;
    }
    if (!selectedRunId || !runOutcomes.some((run) => run.run_id === selectedRunId)) {
      setSelectedRunId(runOutcomes[0].run_id);
    }
  }, [runOutcomes, selectedRunId]);

  const {
    data: taskDetail,
    isFetching: isFetchingTaskDetail,
  } = useGetPanopticonTaskDetailQuery(selectedTaskId ?? '', {
    skip: !selectedTaskId,
  });

  const {
    data: runDetail,
    isFetching: isFetchingRunDetail,
  } = useGetPanopticonRunDetailQuery(selectedRunId ?? '', {
    skip: !selectedRunId,
  });

  const {
    data: patternsData,
    isFetching: isFetchingPatterns,
    refetch: refetchPatterns,
  } = useGetPanopticonPatternsQuery(undefined, {
    pollingInterval: 30000,
  });
  const patterns = React.useMemo(
    () => [...(patternsData?.patterns ?? [])].sort((left, right) => {
      const leftWindowRank = PATTERN_WINDOW_ORDER[left.window_name] ?? Number.MAX_SAFE_INTEGER;
      const rightWindowRank = PATTERN_WINDOW_ORDER[right.window_name] ?? Number.MAX_SAFE_INTEGER;
      if (leftWindowRank !== rightWindowRank) return leftWindowRank - rightWindowRank;
      if (left.count !== right.count) return right.count - left.count;
      const leftTagKind = left.tag_kind ?? 'defect';
      const rightTagKind = right.tag_kind ?? 'defect';
      if (leftTagKind !== rightTagKind) return leftTagKind.localeCompare(rightTagKind);
      return resolvePatternTitle(left).localeCompare(resolvePatternTitle(right));
    }),
    [patternsData?.patterns],
  );

  const {
    data: backfillsData,
    isFetching: isFetchingBackfills,
    refetch: refetchBackfills,
  } = useGetPanopticonBackfillsQuery(undefined, {
    pollingInterval: 10000,
  });
  const backfills = backfillsData?.backfills ?? [];

  const [createBackfill, { isLoading: isCreatingBackfill }] =
    useCreatePanopticonBackfillMutation();
  const [cancelBackfill, { isLoading: isCancellingBackfill }] =
    useCancelPanopticonBackfillMutation();

  const flaggedCount = coverages.filter((coverage) => coverage.needs_attention).length;
  const selectedSummary = asString(asRecord(selectedCoverage?.current_outcome).summary);
  const selectedSnapshots = asArray<Record<string, unknown>>(selectedCoverage?.history ?? []);
  const selectedTask = taskDetail?.task ?? null;
  const selectedRun = runDetail?.run ?? null;
  const selectedMatterLabel = matterLabel(
    selectedCoverage?.matter_name,
    selectedCoverage?.matter_id,
  );
  const selectedCoverageScopeLabel = selectedCoverage
    ? `${selectedMatterLabel} • ${shortId(selectedCoverage.coverage_id)}`
    : 'No coverage selected';

  const handleRefresh = () => {
    refetchCoverages();
    refetchCoverageDetail();
    refetchPatterns();
    refetchBackfills();
  };

  const handleCreateBackfill = async () => {
    if (!windowStart || !windowEnd) {
      toast.error('Window start and end are required');
      return;
    }

    const payload: CreatePanopticonBackfillRequest = {
      window_start: toIsoString(windowStart),
      window_end: toIsoString(windowEnd),
      dry_run: dryRun,
      inline,
      use_read_replica: useReadReplica,
      max_concurrent_coverages: Math.min(
        BACKFILL_MAX_CONCURRENT_COVERAGES,
        Math.max(1, maxConcurrentCoverages),
      ),
      coverage_ids:
        scopeToSelectedCoverage && selectedCoverageId ? [selectedCoverageId] : undefined,
    };

    try {
      await createBackfill(payload).unwrap();
      toast.success(
        scopeToSelectedCoverage && selectedCoverageId
          ? 'Panopticon backfill started for the selected coverage'
          : 'Panopticon backfill started for the full window',
      );
    } catch {
      toast.error('Unable to start Panopticon backfill');
    }
  };

  const handleCancelBackfill = async (runId: string) => {
    try {
      await cancelBackfill(runId).unwrap();
      toast.success('Backfill cancellation requested');
    } catch {
      toast.error('Unable to cancel backfill');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 px-6 py-8 dark:bg-zinc-950">
      <div className="mx-auto max-w-[1680px] space-y-8">
        <section className="overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          <div className="bg-[linear-gradient(135deg,rgba(15,118,110,0.16),rgba(17,24,39,0)_48%),linear-gradient(180deg,rgba(24,24,27,0.03),rgba(24,24,27,0))] px-6 py-6 dark:bg-[linear-gradient(135deg,rgba(20,184,166,0.18),rgba(17,24,39,0)_50%),linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0))]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-600 dark:border-zinc-700 dark:bg-zinc-950/50 dark:text-zinc-300">
                  <Activity className="h-3.5 w-3.5" />
                  Panopticon Outcomes
                </div>
                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                  Coverage-first terminal outcome console
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-600 dark:text-zinc-300">
                  Browse coverage outcomes, drill into task and run evaluations, inspect historical LOR snapshots, and launch event-driven eval backfills from one admin surface.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button outline onClick={handleRefresh} disabled={isFetchingCoverages || isFetchingCoverageDetail}>
                  <RefreshCw
                    data-slot="icon"
                    className={isFetchingCoverages || isFetchingCoverageDetail ? 'animate-spin' : undefined}
                  />
                  Refresh
                </Button>
              </div>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <StatCard label="Coverages" value={coverages.length} />
              <StatCard label="Attention" value={flaggedCount} tone={flaggedCount > 0 ? 'attention' : 'default'} />
              <StatCard label="Tagged themes" value={patterns.length} />
              <StatCard label="Backfills" value={backfills.length} />
            </div>
          </div>
        </section>

        <div className="space-y-6">
          {selectedCoverage ? (
            <>
              <section className="rounded-[24px] border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                <div className="flex flex-col gap-4 border-b border-zinc-200 px-6 py-5 dark:border-zinc-800 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge color={healthBadgeColor(selectedCoverage.health_status)}>
                        {selectedCoverage.health_status}
                      </Badge>
                      {selectedCoverage.needs_attention ? (
                        <Badge color="amber">Needs attention</Badge>
                      ) : (
                        <Badge color="green">Stable</Badge>
                      )}
                    </div>
                    <h2 className="mt-3 text-2xl font-semibold text-zinc-950 dark:text-zinc-50">
                      Coverage {selectedCoverage.coverage_id}
                    </h2>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-600 dark:text-zinc-300">
                      {selectedCoverage.matter_id ? (
                        <Link
                          to={`/matters/${selectedCoverage.matter_id}`}
                          className="font-medium text-blue-600 hover:text-blue-500"
                        >
                          {selectedMatterLabel}
                        </Link>
                      ) : (
                        <span>{selectedMatterLabel}</span>
                      )}
                      <span>Latest run {shortId(selectedCoverage.latest_run_id)}</span>
                    </div>
                  </div>

                  {isLoadingCoverageDetail || isFetchingCoverageDetail ? (
                    <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
                  ) : null}
                </div>

                <div className="grid gap-3 px-6 py-5 md:grid-cols-2 xl:grid-cols-4">
                  <OutcomeRow label="Evaluated" value={formatDateTime(selectedCoverage.evaluated_at)} />
                  <OutcomeRow label="Coverage status" value={selectedCoverage.coverage_status} />
                  <OutcomeRow label="Task outcomes" value={taskOutcomes.length} />
                  <OutcomeRow label="Run outcomes" value={runOutcomes.length} />
                </div>

                <div className="border-t border-zinc-200 px-6 py-5 dark:border-zinc-800">
                  <p className="text-sm leading-6 text-zinc-700 dark:text-zinc-300">
                    {truncate(selectedSummary, 420)}
                  </p>
                </div>
              </section>

              <section className="grid gap-6 xl:grid-cols-2">
                <div className="rounded-[24px] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                  <div className="flex items-center gap-3">
                    <Database className="h-4 w-4 text-zinc-500" />
                    <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                      Task Outcomes
                    </h3>
                    <Badge color="zinc">{taskOutcomes.length}</Badge>
                  </div>
                  <div className="mt-4 space-y-3">
                    {taskOutcomes.map((task) => (
                      <TaskListItem
                        key={task.task_id}
                        task={task}
                        selected={task.task_id === selectedTaskId}
                        onSelect={() => {
                          setSelectedTaskId(task.task_id);
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div className="rounded-[24px] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                  <div className="flex items-center gap-3">
                    <Workflow className="h-4 w-4 text-zinc-500" />
                    <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                      Run Outcomes
                    </h3>
                    <Badge color="zinc">{runOutcomes.length}</Badge>
                  </div>
                  <div className="mt-4 space-y-3">
                    {runOutcomes.map((run) => (
                      <RunListItem
                        key={run.run_id}
                        run={run}
                        selected={run.run_id === selectedRunId}
                        onSelect={() => {
                          setSelectedRunId(run.run_id);
                        }}
                      />
                    ))}
                  </div>
                </div>
              </section>

              <section className="grid gap-6 xl:grid-cols-2">
                <div className="space-y-4 rounded-[24px] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                  <div className="flex items-center gap-3">
                    <Sparkles className="h-4 w-4 text-zinc-500" />
                    <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                      Selected Task
                    </h3>
                    {isFetchingTaskDetail ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  </div>
                  {selectedTask ? (
                    <>
                      <div className="grid gap-3 md:grid-cols-2">
                        <OutcomeRow label="Task type" value={selectedTask.task_type} />
                        <OutcomeRow label="Classification" value={selectedTask.verdict} />
                        <OutcomeRow label="Revision" value={selectedTask.terminal_revision} />
                      </div>
                      <JsonPanel label="Task outcome JSON" value={selectedTask.current_outcome} defaultOpen />
                      <JsonPanel label="Task history JSON" value={selectedTask.history} />
                    </>
                  ) : (
                    <div className="text-sm text-zinc-500 dark:text-zinc-400">
                      Select a task outcome to inspect it.
                    </div>
                  )}
                </div>

                <div className="space-y-4 rounded-[24px] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                  <div className="flex items-center gap-3">
                    <Activity className="h-4 w-4 text-zinc-500" />
                    <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                      Selected Run
                    </h3>
                    {isFetchingRunDetail ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  </div>
                  {selectedRun ? (
                    <>
                      <div className="grid gap-3 md:grid-cols-2">
                        <OutcomeRow label="Status" value={selectedRun.status} />
                        <OutcomeRow label="Classification" value={selectedRun.verdict} />
                        <OutcomeRow label="Workflow" value={selectedRun.workflow_id} />
                      </div>
                      <JsonPanel label="Run outcome JSON" value={selectedRun.current_outcome} defaultOpen />
                      <JsonPanel label="Run steps" value={selectedRun.steps ?? []} />
                    </>
                  ) : (
                    <div className="text-sm text-zinc-500 dark:text-zinc-400">
                      Select a run outcome to inspect it.
                    </div>
                  )}
                </div>
              </section>

              <section className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr),minmax(360px,0.8fr)]">
                <div className="space-y-4 rounded-[24px] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                  <div className="flex items-center gap-3">
                    <History className="h-4 w-4 text-zinc-500" />
                    <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                      Historical Coverage Snapshots
                    </h3>
                    <Badge color="zinc">{selectedSnapshots.length}</Badge>
                  </div>
                  {selectedSnapshots.length === 0 ? (
                    <div className="text-sm text-zinc-500 dark:text-zinc-400">
                      No historical snapshots stored yet.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedSnapshots.slice().reverse().slice(0, 8).map((snapshot, index) => (
                        <JsonPanel
                          key={`${index}-${asString(snapshot.snapshot_at) ?? 'snapshot'}`}
                          label={`Snapshot ${formatDateTime(asString(snapshot.snapshot_at))}`}
                          value={snapshot}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-4 rounded-[24px] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-4 w-4 text-zinc-500" />
                    <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                      Schema Patch Proposals
                    </h3>
                    <Badge color="zinc">{selectedCoverage.schema_patch_proposals.length}</Badge>
                  </div>
                  {selectedCoverage.schema_patch_proposals.length === 0 ? (
                    <div className="text-sm text-zinc-500 dark:text-zinc-400">
                      No schema patch proposals on this coverage.
                    </div>
                  ) : (
                    <JsonPanel
                      label="Coverage schema patch proposals"
                      value={selectedCoverage.schema_patch_proposals}
                      defaultOpen
                    />
                  )}
                </div>
              </section>
            </>
          ) : (
            <div className="rounded-[24px] border border-dashed border-zinc-300 bg-white px-6 py-16 text-center dark:border-zinc-700 dark:bg-zinc-900">
              <Workflow className="mx-auto h-10 w-10 text-zinc-400" />
              <h2 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                Select a coverage
              </h2>
              <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                Pick a coverage outcome to inspect its task and run evidence.
              </p>
            </div>
          )}

          <section className="rounded-[24px] border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                  Coverage Table
                </h2>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                  Sort and scan the latest persisted terminal outcomes.
                </p>
              </div>
              {isLoadingCoverages ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            </div>

            {sortedCoverages.length === 0 && !isLoadingCoverages ? (
              <div className="px-5 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
                No coverage outcomes found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
                  <thead className="bg-zinc-50/80 dark:bg-zinc-950/50">
                    <tr>
                      <th className="px-4 py-3">
                        <CoverageSortHeader
                          label="Coverage"
                          active={coverageSortKey === 'coverage'}
                          direction={coverageSortDirection}
                          onClick={() => handleCoverageSort('coverage')}
                        />
                      </th>
                      <th className="px-4 py-3">
                        <CoverageSortHeader
                          label="Matter"
                          active={coverageSortKey === 'matter'}
                          direction={coverageSortDirection}
                          onClick={() => handleCoverageSort('matter')}
                        />
                      </th>
                      <th className="px-4 py-3">
                        <CoverageSortHeader
                          label="Health"
                          active={coverageSortKey === 'health'}
                          direction={coverageSortDirection}
                          onClick={() => handleCoverageSort('health')}
                        />
                      </th>
                      <th className="px-4 py-3">
                        <CoverageSortHeader
                          label="Attention"
                          active={coverageSortKey === 'attention'}
                          direction={coverageSortDirection}
                          onClick={() => handleCoverageSort('attention')}
                        />
                      </th>
                      <th className="px-4 py-3">
                        <CoverageSortHeader
                          label="Coverage Status"
                          active={coverageSortKey === 'coverage_status'}
                          direction={coverageSortDirection}
                          onClick={() => handleCoverageSort('coverage_status')}
                        />
                      </th>
                      <th className="px-4 py-3">
                        <CoverageSortHeader
                          label="Latest Run"
                          active={coverageSortKey === 'latest_run'}
                          direction={coverageSortDirection}
                          onClick={() => handleCoverageSort('latest_run')}
                        />
                      </th>
                      <th className="px-4 py-3">
                        <CoverageSortHeader
                          label="Evaluated"
                          active={coverageSortKey === 'evaluated_at'}
                          direction={coverageSortDirection}
                          onClick={() => handleCoverageSort('evaluated_at')}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                        Summary
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                    {sortedCoverages.map((coverage) => {
                      const summary = asString(asRecord(coverage.current_outcome).summary);
                      const isSelected = coverage.coverage_id === selectedCoverageId;
                      const resolvedMatterLabel = matterLabel(
                        coverage.matter_name,
                        coverage.matter_id,
                      );

                      return (
                        <tr
                          key={coverage.coverage_id}
                          onClick={() => {
                            setSelectedCoverageId(coverage.coverage_id);
                          }}
                          className={[
                            'cursor-pointer align-top transition',
                            isSelected
                              ? 'bg-orange-50 dark:bg-orange-950/20'
                              : 'hover:bg-zinc-50 dark:hover:bg-zinc-950/40',
                          ].join(' ')}
                        >
                          <td className="px-4 py-4 text-sm font-semibold text-zinc-950 dark:text-zinc-50">
                            Coverage {shortId(coverage.coverage_id)}
                          </td>
                          <td className="px-4 py-4 text-sm text-zinc-700 dark:text-zinc-300">
                            <div className="min-w-[220px]">
                              {coverage.matter_id ? (
                                <Link
                                  to={`/matters/${coverage.matter_id}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                  className="font-medium text-blue-600 hover:text-blue-500"
                                >
                                  {resolvedMatterLabel}
                                </Link>
                              ) : (
                                <span>{resolvedMatterLabel}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <Badge color={healthBadgeColor(coverage.health_status)}>
                              {coverage.health_status}
                            </Badge>
                          </td>
                          <td className="px-4 py-4">
                            <Badge color={coverage.needs_attention ? 'amber' : 'green'}>
                              {coverage.needs_attention ? 'Needs attention' : 'Stable'}
                            </Badge>
                          </td>
                          <td className="px-4 py-4 text-sm text-zinc-700 dark:text-zinc-300">
                            {coverage.coverage_status ?? '—'}
                          </td>
                          <td className="px-4 py-4 text-sm text-zinc-700 dark:text-zinc-300">
                            {shortId(coverage.latest_run_id)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-zinc-700 dark:text-zinc-300">
                            {formatDateTime(coverage.evaluated_at)}
                          </td>
                          <td className="max-w-[420px] px-4 py-4 text-sm leading-6 text-zinc-700 dark:text-zinc-300">
                            {truncate(summary, 180)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1.05fr),minmax(0,0.95fr)]">
          <div className="rounded-[24px] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex items-center gap-3">
              <Clock3 className="h-4 w-4 text-zinc-500" />
              <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                Eval Trigger Backfills
              </h3>
              {isFetchingBackfills ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label
                htmlFor="panopticon-window-start"
                className="text-sm text-zinc-600 dark:text-zinc-300"
              >
                Window start
                <input
                  id="panopticon-window-start"
                  type="datetime-local"
                  value={windowStart}
                  onChange={(event) => {
                    setWindowStart(event.target.value);
                  }}
                  className="mt-2 w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
                />
              </label>
              <label
                htmlFor="panopticon-window-end"
                className="text-sm text-zinc-600 dark:text-zinc-300"
              >
                Window end
                <input
                  id="panopticon-window-end"
                  type="datetime-local"
                  value={windowEnd}
                  onChange={(event) => {
                    setWindowEnd(event.target.value);
                  }}
                  className="mt-2 w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
                />
              </label>
              <label
                htmlFor="panopticon-max-concurrency"
                className="text-sm text-zinc-600 dark:text-zinc-300"
              >
                Max concurrent evals
                <input
                  id="panopticon-max-concurrency"
                  type="number"
                  min={1}
                  max={BACKFILL_MAX_CONCURRENT_COVERAGES}
                  value={maxConcurrentCoverages}
                  onChange={(event) => {
                    const parsed = Number.parseInt(event.target.value, 10);
                    setMaxConcurrentCoverages(
                      Number.isNaN(parsed)
                        ? 1
                        : Math.min(
                            BACKFILL_MAX_CONCURRENT_COVERAGES,
                            Math.max(1, parsed),
                          ),
                    );
                  }}
                  className="mt-2 w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
                />
              </label>
            </div>

            <div className="mt-4 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-300">
              Panopticon backfills replay the terminal events that would have started evaluation in the selected window, then enqueue those task and run eval workflows. Persisted Temporal workflows are the default; turn on dry run only when you want a read-only preview.
            </div>

            <div className="mt-4 flex flex-wrap gap-4 text-sm text-zinc-600 dark:text-zinc-300">
              <label
                htmlFor="panopticon-scope-selected-coverage"
                className="inline-flex items-center gap-2"
              >
                <input
                  id="panopticon-scope-selected-coverage"
                  type="checkbox"
                  checked={scopeToSelectedCoverage}
                  onChange={(event) => setScopeToSelectedCoverage(event.target.checked)}
                  disabled={!selectedCoverageId}
                />
                Limit to selected coverage
              </label>
              <label
                htmlFor="panopticon-dry-run"
                className="inline-flex items-center gap-2"
              >
                <input
                  id="panopticon-dry-run"
                  type="checkbox"
                  checked={dryRun}
                  onChange={(event) => setDryRun(event.target.checked)}
                />
                Dry run
              </label>
              <label
                htmlFor="panopticon-inline"
                className="inline-flex items-center gap-2"
              >
                <input
                  id="panopticon-inline"
                  type="checkbox"
                  checked={inline}
                  onChange={(event) => setInline(event.target.checked)}
                />
                Run inline instead of workflow
              </label>
              <label
                htmlFor="panopticon-read-replica"
                className="inline-flex items-center gap-2"
              >
                <input
                  id="panopticon-read-replica"
                  type="checkbox"
                  checked={useReadReplica}
                  onChange={(event) => setUseReadReplica(event.target.checked)}
                />
                Use read replica
              </label>
            </div>

            <div className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">
              Scope:{' '}
              {scopeToSelectedCoverage && selectedCoverageId
                ? `Selected coverage only (${selectedCoverageScopeLabel})`
                : 'All coverages with matching Panopticon trigger events in the window'}
            </div>

            <div className="mt-4">
              <Button onClick={handleCreateBackfill} disabled={isCreatingBackfill}>
                {isCreatingBackfill ? (
                  <Loader2 data-slot="icon" className="animate-spin" />
                ) : (
                  <History data-slot="icon" />
                )}
                Start backfill
              </Button>
            </div>

            <div className="mt-6 space-y-3">
              {backfills.map((backfill) => (
                <BackfillRow
                  key={backfill.id}
                  backfill={backfill}
                  onCancel={handleCancelBackfill}
                  cancelling={isCancellingBackfill}
                />
              ))}
            </div>
          </div>

          <div className="rounded-[24px] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex items-center gap-3">
              <Sparkles className="h-4 w-4 text-zinc-500" />
              <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                Tagged Finding Themes
              </h3>
              {isFetchingPatterns ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {patterns.slice(0, 12).map((pattern) => (
                <PatternRow key={pattern.id} pattern={pattern} />
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
